import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { shareLink } from "@/lib/publicUrl";
import {
  ArrowLeft, Lightning, Copy, Check, Video, Crown, CheckCircle, PauseCircle, PlayCircle, Warning,
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import SnookerScorer from "@/components/SnookerScorer";
import SubmitResultDialog from "@/components/match/SubmitResultDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LiveMatch() {
  const { matchId } = useParams();
  const { user } = useAuth();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [canScore, setCanScore] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);
  const [busy, setBusy] = useState(false);

  const fetchMatch = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/matches/${matchId}`);
      setMatch(response.data);
    } catch (error) {
      console.error("Error fetching match:", error);
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  useEffect(() => {
    fetchMatch();
    const interval = setInterval(fetchMatch, 3000);
    return () => clearInterval(interval);
  }, [fetchMatch]);

  // Check if user can score based on match scoring_mode
  useEffect(() => {
    if (user && match) {
      if (user.is_admin) {
        setCanScore(true);
        return;
      }
      if (match.referee_user_id && match.referee_user_id === user.user_id) {
        setCanScore(true);
        return;
      }
      // Self-scoring: claimed-profile player in this match
      const mode = match.scoring_mode || match.tournament_scoring_mode || "self";
      if (mode === "self" && user.claimed_player_id &&
          (user.claimed_player_id === match.player1_id || user.claimed_player_id === match.player2_id)) {
        setCanScore(true);
        return;
      }
      setCanScore(false);
    }
  }, [user, match]);

  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase() || '??';

  const copyOBSLink = (style) => {
    const obsUrl = shareLink(`tools/obs/${matchId}?style=${style}`);
    navigator.clipboard.writeText(obsUrl);
    setCopied(true);
    toast.success("OBS link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!match) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Match not found</p>
        <Link to="/matches/live"><Button variant="outline" className="mt-4"><ArrowLeft className="w-4 h-4 mr-2" />Back</Button></Link>
      </div>
    );
  }

  const currentFrame = match.frames?.find(f => !f.is_complete) || match.frames?.[match.frames.length - 1];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <Link to="/matches/live">
        <Button variant="ghost" className="text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4 mr-2" />Back to Live Matches
        </Button>
      </Link>

      {/* Match Header */}
      <Card className="bg-card border-border overflow-hidden">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {match.status === "live" && (
                <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                  <span className="relative flex h-2 w-2 mr-1">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                  </span>LIVE
                </Badge>
              )}
              {match.status === "completed" && <Badge className="bg-primary/20 text-primary">Completed</Badge>}
              <span className="text-lg font-medium">{match.tournament_name}</span>
            </div>
            <Badge variant="outline" className="capitalize">{match.round?.replace('_', ' ')}</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="flex-1 text-center">
              <Avatar className="h-24 w-24 mx-auto border-4 border-border">
                <AvatarImage src={match.player1_photo} />
                <AvatarFallback className="text-2xl">{getInitials(match.player1_name)}</AvatarFallback>
              </Avatar>
              <h2 className="mt-4 text-xl font-heading font-bold">{match.player1_name}</h2>
              {match.winner_id === match.player1_id && <Crown className="w-5 h-5 text-secondary mx-auto mt-1" weight="fill" />}
            </div>
            <div className="px-8">
              <div className="flex items-center gap-6">
                <span className={`text-6xl font-heading font-bold ${match.winner_id === match.player1_id ? 'text-primary' : ''}`}>{match.player1_score}</span>
                <span className="text-3xl text-muted-foreground">-</span>
                <span className={`text-6xl font-heading font-bold ${match.winner_id === match.player2_id ? 'text-primary' : ''}`}>{match.player2_score}</span>
              </div>
              <p className="text-center text-sm text-muted-foreground mt-2">Best of {match.best_of || 5}</p>
            </div>
            <div className="flex-1 text-center">
              <Avatar className="h-24 w-24 mx-auto border-4 border-border">
                <AvatarImage src={match.player2_photo} />
                <AvatarFallback className="text-2xl">{getInitials(match.player2_name)}</AvatarFallback>
              </Avatar>
              <h2 className="mt-4 text-xl font-heading font-bold">{match.player2_name}</h2>
              {match.winner_id === match.player2_id && <Crown className="w-5 h-5 text-secondary mx-auto mt-1" weight="fill" />}
            </div>
          </div>
          {match.status === "completed" && (
            <div className="text-center py-4 bg-primary/10 rounded-lg">
              <p className="text-lg font-medium text-primary">
                {match.winner_id === match.player1_id ? match.player1_name : match.player2_name} wins!
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Scoring Controls — shape depends on effective scoring_type */}
      {(() => {
        if (!canScore || ["completed", "walkover", "no_result"].includes(match.status)) return null;

        const effectiveType = match.scoring_type || match.tournament_scoring_type || "submission";
        const isSubmission = effectiveType === "submission";
        const isLive = effectiveType === "live" && match.status === "live";

        const startLive = async () => {
          setBusy(true);
          try {
            await axios.post(`${API}/matches/${matchId}/start-live-scoring`, {}, { withCredentials: true });
            toast.success("Live scoring started");
            fetchMatch();
          } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to start live scoring");
          } finally { setBusy(false); }
        };
        const pauseMatch = async () => {
          if (!window.confirm("Pause this match? It will stop appearing in Match Centre. Scores are preserved.")) return;
          setBusy(true);
          try {
            await axios.post(`${API}/matches/${matchId}/pause`, {}, { withCredentials: true });
            toast.success("Match paused");
            fetchMatch();
          } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to pause");
          } finally { setBusy(false); }
        };

        return (
          <>
            {/* Scoring-type banner + admin action row */}
            <Card className="bg-card border-border" data-testid="scoring-controls">
              <CardContent className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Badge variant="outline" className={isSubmission ? "border-primary/40 text-primary" : "border-destructive/40 text-destructive"}>
                      {isSubmission ? "Manual Submission" : "Live Scoring"}
                    </Badge>
                    <span className="text-muted-foreground">
                      {isSubmission
                        ? "Enter the final frame score after the match."
                        : "Shot-by-shot scoring active."}
                    </span>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {isSubmission && (
                      <Button
                        size="sm" onClick={() => setShowSubmit(true)} disabled={busy}
                        data-testid="open-submit-result"
                      >
                        <CheckCircle className="w-4 h-4 mr-1.5" weight="fill" />Enter Result
                      </Button>
                    )}
                    {isSubmission && user?.is_admin && (
                      <Button
                        size="sm" variant="outline" disabled={busy}
                        onClick={startLive}
                        data-testid="switch-to-live"
                      >
                        <PlayCircle className="w-4 h-4 mr-1.5" />Switch to Live
                      </Button>
                    )}
                    {match.status === "live" && (
                      <Button
                        size="sm" variant="outline"
                        className="border-destructive/40 text-destructive hover:bg-destructive/10"
                        disabled={busy} onClick={pauseMatch}
                        data-testid="pause-match-btn"
                      >
                        <PauseCircle className="w-4 h-4 mr-1.5" />Pause Match
                      </Button>
                    )}
                    {match.status === "scheduled" && match.live_started_at && !isSubmission && (
                      <Button
                        size="sm" disabled={busy} onClick={startLive}
                        data-testid="resume-match-btn"
                      >
                        <PlayCircle className="w-4 h-4 mr-1.5" weight="fill" />Resume Match
                      </Button>
                    )}
                  </div>
                </div>
                {match.paused_at && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                    <Warning className="w-3.5 h-3.5 text-secondary" />
                    Paused — not showing in Match Centre. Click "Resume Match" to bring it back live.
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Live frame engine (only in live mode + match is currently live) */}
            {isLive && (
              <SnookerScorer matchId={matchId} match={match} onScoreUpdate={fetchMatch} />
            )}
          </>
        );
      })()}

      {/* Current Frame (for spectators) */}
      {!canScore && currentFrame && match.status === "live" && (
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lightning className="w-5 h-5 text-secondary" weight="fill" />
              Frame {currentFrame.frame_number}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center gap-8 py-4">
              <div className="text-center">
                <p className="text-4xl font-heading font-bold">{currentFrame.player1_points}</p>
                <p className="text-sm text-muted-foreground">{match.player1_name}</p>
              </div>
              <div className="text-2xl text-muted-foreground">-</div>
              <div className="text-center">
                <p className="text-4xl font-heading font-bold">{currentFrame.player2_points}</p>
                <p className="text-sm text-muted-foreground">{match.player2_name}</p>
              </div>
            </div>
            {currentFrame.current_turn && (
              <p className="text-center text-sm text-muted-foreground">
                At the table: <span className="text-primary font-medium">
                  {currentFrame.current_turn === 1 ? match.player1_name : match.player2_name}
                </span>
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Frame History */}
      {match.frames && match.frames.length > 0 && (
        <Card className="bg-card border-border">
          <CardHeader><CardTitle>Frame History</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {match.frames.map((frame) => (
                <div key={`frame-${frame.frame_number}`} className={`p-3 rounded-lg border ${
                  frame.is_complete
                    ? frame.winner_id === match.player1_id ? 'border-primary/30 bg-primary/5' : 'border-secondary/30 bg-secondary/5'
                    : 'border-border bg-muted/30'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Frame {frame.frame_number}</span>
                    {frame.is_complete && (
                      <Badge variant="outline" className="text-xs">
                        {frame.winner_id === match.player1_id ? match.player1_name?.split(' ')[0] : match.player2_name?.split(' ')[0]}
                      </Badge>
                    )}
                    {!frame.is_complete && frame.frame_number === (match.frames.findIndex(f => !f.is_complete) + 1) && (
                      <Badge className="bg-secondary/20 text-secondary text-[10px]">Playing</Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-heading font-bold">{frame.player1_points}</span>
                    <span className="text-muted-foreground">-</span>
                    <span className="font-heading font-bold">{frame.player2_points}</span>
                  </div>
                  {(frame.player1_breaks?.length > 0 || frame.player2_breaks?.length > 0) && (
                    <div className="mt-2 text-xs text-muted-foreground">
                      {frame.player1_breaks?.length > 0 && <p>P1 breaks: {frame.player1_breaks.join(', ')}</p>}
                      {frame.player2_breaks?.length > 0 && <p>P2 breaks: {frame.player2_breaks.join(', ')}</p>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* OBS Links */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Video className="w-5 h-5 text-primary" />OBS Overlay Links</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">Copy links for OBS Browser Sources.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 rounded-lg border border-border">
              <h4 className="font-medium mb-2">Minimal</h4>
              <Button variant="outline" onClick={() => copyOBSLink('minimal')} className="w-full">
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}Copy
              </Button>
            </div>
            <div className="p-4 rounded-lg border border-border">
              <h4 className="font-medium mb-2">Full</h4>
              <Button variant="outline" onClick={() => copyOBSLink('full')} className="w-full">
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}Copy
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <SubmitResultDialog
        open={showSubmit}
        onOpenChange={setShowSubmit}
        match={match}
        onSubmitted={fetchMatch}
      />
    </motion.div>
  );
}
